import { Box, Paper, Stack, Typography } from '@mui/material';

/**
 * Las primitivas del estilo de la casa.
 *
 * Son las reglas de `frontend/docs/ESTILO_DE_LA_CASA.md` hechas código. Existen
 * por un motivo práctico, no estético: una regla escrita que hay que recordar se
 * incumple, una que viene en un componente más corto que escribirla a mano se
 * cumple sola.
 *
 * No son un sistema de diseño ni pretenden cubrir la pantalla entera: son las
 * cinco decisiones que se repetían mal en cada lugar.
 */

/** Alto mínimo de todo lo que se toca: botones, filas accionables, íconos. */
export const ALTURA_TACTIL = 44;

/** Los tonos de un número. Oscuros a propósito: el gris claro sobre blanco no llega a 4.5:1. */
const TONO = {
  neutro: 'text.primary',
  bueno: 'success.dark',
  malo: 'error.dark',
  atento: 'warning.dark',
  tenue: 'text.disabled',
};

const TAMANO = {
  s: { fontSize: 13, fontWeight: 600 },
  m: { fontSize: 16, fontWeight: 600 },
  l: { fontSize: 26, fontWeight: 800 },
};

/**
 * Un número.
 *
 * Plus Jakarta Sans y `tabular-nums` SIEMPRE: sin tabular, `$7.662.537` y
 * `$535.209` no alinean sus dígitos y la columna deja de poder compararse de un
 * vistazo, que es para lo único que sirve ponerlos en columna.
 *
 * El tono va en el número y no en el fondo: un bloque teñido se lee como alerta
 * y gasta el recurso — cuando todo está pintado, nada resalta.
 */
export function Cifra({ children, tono = 'neutro', tamano = 'm', sx, ...rest }) {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.01em',
        color: TONO[tono] || TONO.neutro,
        ...(TAMANO[tamano] || TAMANO.m),
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/**
 * La etiqueta de un dato.
 *
 * Chica y gris a propósito: el rótulo pesa MENOS que el dato. Con un título de
 * 16 px arriba de un número de 14, el ojo lee la etiqueta y saltea el valor, que
 * es justo lo que el bloque vino a mostrar.
 */
export function Rotulo({ children, sx, ...rest }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: 'block',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        lineHeight: 1.5,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Typography>
  );
}

/**
 * Un grupo de celdas que son caras de la misma cosa.
 *
 * Una grilla con `gap: 1px` sobre el color del divisor: las celdas quedan
 * separadas por una línea de un píxel y encerradas en un solo borde. Tres
 * tarjetas sueltas dicen «tres cosas distintas»; una banda dice «una cosa con
 * tres caras», que es lo que los ejes de una obra son. Y ahorra el padding
 * exterior de cada tarjeta, que en una pantalla densa es media fila más.
 */
export function Banda({ columnas = 4, children, sx, ...rest }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(columnas, 2)}, minmax(0, 1fr))`,
          md: `repeat(${columnas}, minmax(0, 1fr))`,
        },
        gap: '1px',
        bgcolor: 'divider',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/** Una celda de la banda. El fondo es suyo: es lo que deja ver la línea de 1 px. */
export function Celda({ children, sx, ...rest }) {
  return (
    <Box sx={{ bgcolor: 'background.paper', px: 2, py: 1.75, ...sx }} {...rest}>
      {children}
    </Box>
  );
}

/**
 * Un bloque con su cabecera.
 *
 * Padding 15–18, no 24: esto es un panel de trabajo y quien lo usa quiere ver
 * más filas, no más aire. La acción vive en la cabecera, a la derecha, en texto
 * y no en botón — un botón por bloque convierte la pantalla en una botonera.
 */
export function Bloque({ titulo, accion, children, sx, ...rest }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, ...sx }} {...rest}>
      {(titulo || accion) && (
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography
            sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 14 }}
          >
            {titulo}
          </Typography>
          {accion}
        </Stack>
      )}
      {children}
    </Paper>
  );
}

/**
 * Lo que se dibuja cuando el vacío ES la noticia.
 *
 * La regla por defecto es otra: **un bloque sin datos no se renderiza**. Una
 * pantalla llena de cajas grises que dicen «no hay movimientos» le enseña a la
 * gente que no tiene nada que decir, y en dos semanas deja de mirarla.
 *
 * Esto es la excepción: cuando no hay nada Y hace falta explicar qué va a
 * aparecer. Una línea, no un bloque.
 */
export function SinNada({ children, sx, ...rest }) {
  return (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        px: 3,
        py: 3.5,
        textAlign: 'center',
        ...sx,
      }}
      {...rest}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, maxWidth: 620, mx: 'auto' }}>
        {children}
      </Typography>
    </Box>
  );
}
